import axios from 'axios';
import { call, all, put, takeLatest, delay, select } from 'redux-saga/effects';
import { isAfter } from 'date-fns';

import history from '~/services/history';
import notification from '~/services/notification';
import promotionService from './promotion-service';
import clientService from '../client/client-service';
import productService from '../product/product-service';
import loadtypeService from '../load-type/load-type-service';
import userService from '~/services/modules/user/user-service';
import domainService from '~/services/modules/domain/domain-service';
import * as promotionActions from './promotion-actions';
import * as productActions from '../product/product-actions';
import * as clientActions from '../client/client-actions';

import {
  GET_ALL_REQUEST,
  GET_BY_ID_REQUEST,
  SAVE_REQUEST,
  EDIT_ITEM,
  DELETE_ITEM,
  GET_FILTER_PRODUCT_REQUEST,
  GET_FILTER_CLIENT_REQUEST,
  GET_FILTER_REWARDS_REQUEST,
  GET_BY_ID_REQUEST_IMPORT,
  EDIT_IMPORTED_ITEM,
  SAVE_IMPORT_REQUEST
} from './promotion-constants';
import { ISA_ERROR, USER_ROLE, PROMOTION_LIST } from '~/constants';
import { formatMessage } from '~/i18n/translate';
import { AppStateType } from '../root-reducer';
import { getClientsProps } from '../client/client-interface';
import rewardsService from '~/services/modules/rewards/rewards-service';

let cancel: any;
const CancelToken = axios.CancelToken;

const INITIAL_VALUES = {
  promotionName: '',
  dateRange: [new Date(), null],
  releaseDate: new Date(),
  items: []
};

const sort = items => {
  return [...items]?.sort((a: any, b: any) => a.externalCode - b.externalCode);
};

const formatEndDate = (date: Date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}T23:59:59.000Z`;
};

export function* queryClients({ queryText }: any) {
  const fields = [
    'externalCode',
    'clientName',
    'name',
    'governmentalCode',
    'blocked',
    'commercialInfo',
    'lastPurchaseDate',
    'nextVisit'
  ];

  yield delay(200);

  if (cancel !== undefined) cancel();

  const cancelToken = new CancelToken(function executor(c) {
    cancel = c;
  });

  try {
    const { externalCode: userExternalCode } = yield select((state: AppStateType) => state.auth.user);

    yield put(clientActions.setLoadingData(true));
    const { data: clients } = yield call(
      clientService.getQuery,
      {
        fields,
        skip: 0,
        limit: 50,
        queryString: queryText,
        userExternalCode
      } as getClientsProps,
      {
        cancelToken
      }
    );

    yield put(promotionActions.getFilterClientSuccess(clients));
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(clientActions.setLoadingData(false));
  }
}

export function* queryProducts({ queryText = '' }: any) {
  yield delay(200);

  if (cancel !== undefined) cancel();

  const cancelToken = new CancelToken(function executor(c) {
    cancel = c;
  });

  try {
    yield put(productActions.setLoadingData(true));
    const { data: products } = yield call(
      productService.getRawProducts,
      {
        skip: 0,
        limit: 100,
        queryString: queryText
      },
      {
        cancelToken
      }
    );

    yield put(promotionActions.getFilterProductSuccess(products));
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(productActions.setLoadingData(false));
  }
}

export function* getRewards() {
  try {
    // yield put(promotionActions.setLoadingData(true));
    const { data: products } = yield call(rewardsService.getAllRewards);

    //.filter(x => x.type === "client")
    yield put(promotionActions.getFilterRewardsSuccess(products));
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    // yield put(promotionActions.setLoadingData(false));
  }
}

export function* getAll({ typeList }: any) {
  try {
    yield put(promotionActions.setLoadingData(true));

    const { page, rowsPerPage, order, orderBy } = yield select(state => state.promotion.list);

    const pagination = {
      skip: page === 0 ? 0 : page * rowsPerPage,
      limit: rowsPerPage,
      order,
      orderBy
    };

    if (typeList === PROMOTION_LIST.REGISTERED) {
      const { data: promotionsRegistered } = yield call(promotionService.getAllRegisteredPromotions, {
        ...pagination
      });

      const { data: rowsCount } = yield call(promotionService.getRegisteredPromotionCount);

      yield put(
        promotionActions.getAllSuccess({
          rows: [
            ...promotionsRegistered.map((item: any) => ({
              ...item,
              isExpired: isAfter(new Date(), new Date(item?.endDate))
            }))
          ],
          rowsCount
        })
      );
    } else {
      const { data: promotionsImported } = yield call(promotionService.getAllImportedPromotions, {
        ...pagination
      });

      const { data: rowsCount } = yield call(promotionService.getImportedPromotionCount);

      yield put(
        promotionActions.getAllSuccess({
          rows: [
            ...promotionsImported.map((item: any) => ({
              ...item,
              isExpired: isAfter(new Date(), new Date(item?.endDate))
            }))
          ],
          rowsCount
        })
      );
    }

    yield put(promotionActions.setLoadingData(false));
  } catch (error) {
    yield put(promotionActions.setLoadingData(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* getById({ id }: any) {
  try {
    yield put(promotionActions.setLoadingData(true));
    const { externalCode: userExternalCode, userSubsidiaries } = yield select((state: AppStateType) => state.auth.user);

    const clientFields = [
      'externalCode',
      'clientName',
      'name',
      'governmentalCode',
      'blocked',
      'commercialInfo',
      'lastPurchaseDate',
      'nextVisit'
    ];

    const [
      { data: clientGroups },
      { data: subsidiaries },
      { data: users },
      { data: activities },
      { data: paymentOptions },
      { data: paymentPlans },
      { data: loadTypes },
      { data: priceTableCode },

      { data: brands },
      { data: categories },
      { data: departments },
      { data: providers },
      { data: sections },
      { data: productLines }
    ] = yield all([
      call(domainService.getClientGroups),
      call(domainService.getSubsidiaries, { userSubsidiaryFilter: userSubsidiaries ?? [] }),
      call(userService.getUsers),
      call(domainService.getActivities),
      call(domainService.getPaymentOptions),
      call(domainService.getPaymentPlans, 0, 30),
      call(loadtypeService.getAll),
      call(domainService.getPriceTableCodes),

      call(domainService.getBrands),
      call(domainService.getCategories),
      call(domainService.getDepartments),
      call(domainService.getProviders),
      call(domainService.getSections),
      call(domainService.getProductLines)
    ]);

    yield queryClients({});
    yield queryProducts({});

    let selects = {
      clientGroup: sort(clientGroups),
      subsidiary: sort(subsidiaries.map(({ name, externalCode }) => ({ externalCode, name }))),
      supervisor: sort(
        users
          .filter((user: any) => user.userRole === USER_ROLE.SUPERVISOR)
          .map(({ name, externalCode }) => ({ externalCode, name }))
      ),
      vendor: sort(
        users
          .filter((user: any) => user.userRole === USER_ROLE.VENDOR || user.userRole === USER_ROLE.TELEMARKETING)
          .map(({ name, externalCode }) => ({ externalCode, name }))
      ),
      activity: sort(activities),
      paymentOption: sort(paymentOptions),
      paymentPlan: sort(paymentPlans),
      loadType: sort(loadTypes),
      //PROMOTION ITEM
      brand: sort(brands) ?? [],
      category: sort(categories) ?? [],
      department: sort(departments) ?? [],
      provider: sort(providers) ?? [],
      section: sort(sections) ?? [],
      productLine: sort(productLines) ?? [],
      priceTableCode: sort(priceTableCode) ?? []
    };

    if (!id) {
      yield put(
        promotionActions.getByIdSuccess({
          data: INITIAL_VALUES,
          selects
        })
      );
    } else {
      const { data } = yield call(promotionService.getRegisteredPromotion, id);

      if (data) {
        const { beginDate, endDate, ...rest } = data;
        const { data: rewards } = yield call(rewardsService.getAllRewards);

        const clientExternalCodes = data.items
          ?.map(item => item['clientExternalCodes'])
          ?.flat(Infinity)
          ?.join(',');

        const { data: clients }: any = clientExternalCodes?.length
          ? yield call(clientService.getQuery, {
              skip: 0,
              limit: 0,
              fields: clientFields,
              clientExternalCodes,
              userExternalCode
            } as getClientsProps)
          : { clients: [] };

        const productExternalCodes = data.items
          ?.map(item => [
            ...['required', 'bonification', 'reward'].map(type => item[type].map(x => x.productExternalCode)),
            ...item['productExternalCodes']
          ])
          ?.flat(Infinity)
          ?.join(',');

        let product;

        if (productExternalCodes?.length > 0) {
          const { data } = yield call(productService.getRawProducts, {
            skip: 0,
            limit: 0,
            productExternalCodes
          });

          product = data;
        } else {
          product = [];
        }

        const payload = {
          data: {
            ...rest,
            dateRange: [new Date(beginDate), new Date(endDate)],
            loadType: loadTypes.find(c => c._id === data?.loadType),
            items: [
              ...data.items.map(item => ({
                ...item,
                mixRules: {
                  ...data?.mixRules,
                  productExternalCode: data?.mixRules?.products?.map(product => product.externalCode)
                },
                required: item?.required?.map(({ productExternalCode, minQuantity }) => {
                  const item = product?.find(x => x.externalCode === productExternalCode?.toString());

                  return {
                    minQuantity,
                    product: item,
                    productExternalCode
                  };
                }),
                bonification: item?.bonification?.map(({ productExternalCode, quantity }) => {
                  const item = product?.find(x => x.externalCode === productExternalCode?.toString());

                  return {
                    quantity,
                    product: item,
                    productExternalCode
                  };
                }),
                reward: item?.reward?.map(({ rewardCode, quantity }) => {
                  const reward = rewards?.find(x => x.code?.toString() === rewardCode?.toString());

                  return {
                    quantity,
                    reward,
                    rewardCode
                  };
                }),
                client: clients,
                product: product.filter(x => item?.productExternalCodes?.includes(x.externalCode)) ?? [],
                priceTableExternalCodes: item?.priceTableCodes ?? [],
                ...Object.assign(
                  {},
                  ...Object.values([
                    'clientGroup',
                    'activity',
                    'subsidiary',
                    'paymentOption',
                    'paymentPlan',
                    'vendor',
                    'supervisor',
                    'brand',
                    'category',
                    'department',
                    'section',
                    'provider'
                  ]).map(type => ({
                    [type]: item?.[`${type}ExternalCodes`]
                      ?.map(externalCode =>
                        selects[type]?.filter(c =>
                          c.externalCode === externalCode ? c.externalCode === externalCode.toString() : ''
                        )
                      )
                      .flat(Infinity)
                  }))
                )
              }))
            ]
          },
          selects
        };

        yield put(promotionActions.getByIdSuccess({ ...payload }));
      }
    }
    yield put(promotionActions.setLoadingData(false));
  } catch (error) {
    yield put(promotionActions.setLoadingData(false));
    yield put(
      promotionActions.getByIdSuccess({
        data: INITIAL_VALUES,
        selects: {
          client: [],
          clientGroup: [],
          subsidiary: [],
          supervisor: [],
          vendor: [],
          activity: [],
          paymentOption: [],
          paymentPlan: [],
          brand: [],
          department: [],
          provider: [],
          section: [],
          product: []
        }
      })
    );
    yield put({ type: ISA_ERROR, error });
  }
}

export function* getByIdImportPromotion({ id }: any) {
  try {
    yield put(promotionActions.setLoadingData(true));
    const user = yield select((state: AppStateType) => state.auth.user);

    const [
      { data: clientGroups },
      { data: subsidiaries },
      { data: users },
      { data: activities },
      { data: paymentOptions },
      { data: paymentPlans },
      { data: loadTypes },

      { data: brands },
      { data: categories },
      { data: departments },
      { data: providers },
      { data: sections },
      { data: productLines }
    ] = yield all([
      call(domainService.getClientGroups),
      call(domainService.getSubsidiaries, { userSubsidiaryFilter: user?.userSubsidiaries ?? [] }),
      call(userService.getUsers),
      call(domainService.getActivities),
      call(domainService.getPaymentOptions),
      call(domainService.getPaymentPlans, 0, 30),
      call(loadtypeService.getAll),

      call(domainService.getBrands),
      call(domainService.getCategories),
      call(domainService.getDepartments),
      call(domainService.getProviders),
      call(domainService.getSections),
      call(domainService.getProductLines)
    ]);

    yield queryClients({});
    yield queryProducts({});

    let selects = {
      clientGroup: sort(clientGroups),
      subsidiary: sort(subsidiaries.map(({ name, externalCode }) => ({ externalCode, name }))),
      supervisor: sort(
        users
          .filter((user: any) => user.userRole === USER_ROLE.SUPERVISOR)
          .map(({ name, externalCode }) => ({ externalCode, name }))
      ),
      vendor: sort(
        users
          .filter((user: any) => user.userRole === USER_ROLE.VENDOR || user.userRole === USER_ROLE.TELEMARKETING)
          .map(({ name, externalCode }) => ({ externalCode, name }))
      ),
      activity: sort(activities),
      paymentOption: sort(paymentOptions),
      paymentPlan: sort(paymentPlans),
      loadType: sort(loadTypes),
      //PROMOTION ITEM
      brand: sort(brands) ?? [],
      category: sort(categories) ?? [],
      department: sort(departments) ?? [],
      provider: sort(providers) ?? [],
      section: sort(sections) ?? [],
      productLine: sort(productLines) ?? []
    };

    if (!id) {
      yield put(
        promotionActions.getByIdSuccess({
          data: INITIAL_VALUES,
          selects
        })
      );
    } else {
      const { data: promotion } = yield call(promotionService.getImportedPromotion, id);

      if (promotion) {
        const { beginDate, endDate, productExternalCode, ...rest } = promotion;
        const { data: rewards } = yield call(rewardsService.getAllRewards);

        const { data: products }: any =
          productExternalCode?.length > 0
            ? yield call(productService.getRawProducts, {
                skip: 0,
                limit: 0
              })
            : { products: [] };

        const payload = {
          data: {
            ...rest,
            ...promotion,
            dateRange: [new Date(beginDate), new Date(endDate)],
            product: products?.filter(x => productExternalCode?.includes(x.externalCode)) ?? [],
            required: promotion?.required?.map(({ productExternalCode, minQuantity }) => {
              const product = products?.find(x => x.externalCode === productExternalCode?.toString());

              return {
                minQuantity,
                product,
                productExternalCode
              };
            }),
            bonification: promotion?.bonification?.map(({ productExternalCode, quantity }) => {
              const product = products?.find(x => x.externalCode === productExternalCode?.toString());

              return {
                quantity,
                product,
                productExternalCode
              };
            }),
            reward: promotion?.reward?.map(({ rewardExternalCode, quantity }) => {
              const reward = rewards?.find(x => x.code.toString() === rewardExternalCode?.toString());

              return {
                quantity,
                reward,
                rewardExternalCode
              };
            }),
            priceTableExternalCodes: promotion?.priceTableCodes ?? [],
            ...Object.assign(
              {},
              ...Object.values([
                'clientGroup',
                'activity',
                'subsidiary',
                'paymentOption',
                'paymentPlan',
                'vendor',
                'supervisor',
                'brand',
                'category',
                'department',
                'section',
                'provider'
              ]).map(type => ({
                [type]: promotion?.[`${type}ExternalCodes`]
                  ?.map(externalCode => selects[type]?.filter(c => c.externalCode === externalCode.toString()))
                  .flat(Infinity)
              }))
            )
          }
        };

        yield put(promotionActions.getByIdSuccess({ ...payload }));
      }
    }

    yield put(promotionActions.setLoadingData(false));
  } catch (error) {
    yield put(promotionActions.setLoadingData(false));
    yield put(
      promotionActions.getByIdSuccess({
        data: INITIAL_VALUES,
        selects: {
          client: [],
          clientGroup: [],
          subsidiary: [],
          supervisor: [],
          vendor: [],
          activity: [],
          paymentOption: [],
          paymentPlan: [],
          brand: [],
          department: [],
          provider: [],
          section: [],
          product: []
        }
      })
    );
    yield put({ type: ISA_ERROR, error });
  }
}

export function* save(data: any) {
  try {
    const { id, payload, files } = data;
    yield put(promotionActions.setLoadingSend(true));

    const {
      dateRange,
      clients,
      clientGroups,
      subsidiaries,
      activities,
      paymentOptions,
      paymentPlans,
      vendors,
      supervisors,
      ...rest
    } = payload;

    let mixRules = null;

    if (payload?.isPromotionMix && payload?.items[0]?.mixRules?.productExternalCode?.length > 0) {
      mixRules = {
        products: payload?.items[0]?.mixRules?.products,
        discountByReachedItemsPercentage: payload?.items[0]?.mixRules?.discountByReachedItemsPercentage
      };
    }

    const promotion = {
      ...rest,
      beginDate: dateRange[0],
      endDate: formatEndDate(dateRange[1]),
      mixRules,
      items: payload?.items?.map(({ index, ...item }) => {
        return {
          financialCost: item?.financialCost,
          discountPercentage: item?.discountPercentage,
          comissionPromo: item?.comissionPromo,
          fixedPrice: item?.fixedPrice,
          finalQuantity: item?.finalQuantity,
          initialQuantity: item?.initialQuantity,
          loadTypeExternalCodes: item?.loadType?.code ? [item?.loadType?.code] : [],
          priceTableCodes: item?.priceTableExternalCodes ?? [],
          required: item?.required?.map(({ product, minQuantity }) => ({
            minQuantity,
            productExternalCode: product?.externalCode
          })),
          bonification: item?.bonification?.map(({ product, quantity }) => ({
            quantity,
            productExternalCode: product?.externalCode
          })),
          reward: item?.reward?.map(({ reward, quantity }) => ({
            quantity,
            rewardCode: reward?.code
          })),
          clientExternalCodes: item?.client?.map(obj => obj.externalCode) ?? [],
          clientGroupExternalCodes: item?.clientGroup?.map(obj => obj.externalCode) ?? [],
          activityExternalCodes: item?.activity?.map(obj => obj.externalCode) ?? [],
          subsidiaryExternalCodes: item?.subsidiary?.map(obj => obj.externalCode) ?? [],
          paymentOptionExternalCodes: item?.paymentOption?.map(obj => obj.externalCode) ?? [],
          paymentPlanExternalCodes: item?.paymentPlan?.map(obj => obj.externalCode) ?? [],
          vendorExternalCodes: item?.vendor?.map(obj => obj.externalCode) ?? [],
          supervisorExternalCodes: item?.supervisor?.map(obj => obj.externalCode) ?? [],
          productExternalCodes: item?.product?.map(obj => obj.externalCode) ?? [],
          brandExternalCodes: item?.brand?.map(obj => obj.externalCode) ?? [],
          categoryExternalCodes: item?.category?.map(obj => obj.externalCode) ?? [],
          departmentExternalCodes: item?.department?.map(obj => obj.externalCode) ?? [],
          sectionExternalCodes: item?.section?.map(obj => obj.externalCode) ?? [],
          providerExternalCodes: item?.provider?.map(obj => obj.externalCode) ?? []
        };
      })
    };

    if (id) {
      let pictures = [];

      if (promotion?.isPromotionKit && files) {
        try {
          const { data } = yield call(promotionService.createPromotionImageKit, {
            file: files,
            promotionGroupExternalCode: promotion?.externalCode
          });
          pictures = data.pictures;
        } catch (error) {
          yield put(promotionActions.setLoadingSend(false));
          yield put({ type: ISA_ERROR, error });
          return;
        }
      }

      const parsedPromotion = {
        ...promotion,
        pictures: files ? pictures : promotion?.pictures ? promotion?.pictures : []
      };

      yield call(promotionService.editPromotion, { _id: id, ...parsedPromotion });
    } else {
      let pictures = [];
      try {
        if (promotion?.isPromotionKit && files) {
          const { data } = yield call(promotionService.createPromotionImageKit, {
            file: files,
            promotionGroupExternalCode: promotion?.externalCode
          });

          pictures = data.pictures;
        }
      } catch (error) {
        yield put(promotionActions.setLoadingSend(false));
        yield put({ type: ISA_ERROR, error });
        return;
      }

      const parsedPromotion = {
        ...promotion,
        pictures: pictures ?? []
      };

      yield call(promotionService.createPromotion, parsedPromotion);
    }

    yield call(history.push, `/engine/promotion/`, {
      date: new Date().getTime()
    });
    yield put(promotionActions.setLoadingSend(false));
    yield call(
      notification.success,
      id ? formatMessage('promotionFormUpdatedSuccess') : formatMessage('promotionFormCreatedSuccess')
    );
  } catch (error) {
    yield put(promotionActions.setLoadingSend(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* saveImported(data: any) {
  try {
    const { id, payload, files } = data;
    yield put(promotionActions.setLoadingSend(true));

    const {
      dateRange,
      clients,
      clientGroups,
      subsidiaries,
      activities,
      paymentOptions,
      paymentPlans,
      vendors,
      supervisors,
      externalCode,
      ...rest
    } = payload;

    // Copia o objeto payload, e retira apenas o product
    const payloadParsed = (({ product, ...rest }) => rest)(payload);

    const promotion = {
      ...payloadParsed,
      beginDate: dateRange[0],
      endDate: formatEndDate(dateRange[1]),
      required: payload?.required?.map(({ product, minQuantity }) => ({
        minQuantity,
        productExternalCode: product?.externalCode
      })),
      bonification: payload?.bonification?.map(({ product, quantity }) => ({
        quantity,
        productExternalCode: product?.externalCode
      })),
      reward: payload?.reward?.map(({ reward, quantity }) => ({
        quantity,
        rewardExternalCode: reward?.code
      })),
      loadTypeExternalCodes: payload?.loadType?.code ? [payload?.loadType?.code] : [],
      priceTableCodes: payload?.priceTableExternalCodes ?? [],
      ...Object.assign(
        {},
        ...Object.values([
          'client',
          'clientGroup',
          'activity',
          'subsidiary',
          'paymentOption',
          'paymentPlan',
          'vendor',
          'supervisor',
          'product',
          'brand',
          'category',
          'department',
          'section',
          'provider'
        ]).map(type => ({
          [`${type}ExternalCodes`]: payload[type]?.map(obj => obj.externalCode)
        }))
      )
    };

    let pictures = [];

    if (promotion?.isPromotionKit && files) {
      const { data } = yield call(promotionService.createPromotionImageKitImported, {
        file: files,
        promotionExternalCode: promotion?.externalCode
      });

      pictures = data.pictures;
    }

    const parsedPromotion = {
      ...promotion,
      pictures: files ? pictures : promotion?.pictures ? promotion?.pictures : []
    };

    yield call(promotionService.editPromotionImported, { _id: id, ...parsedPromotion });

    yield call(history.push, `/engine/promotion/`, {
      date: new Date().getTime()
    });
    yield call(
      notification.success,
      id ? formatMessage('promotionFormUpdatedSuccess') : formatMessage('promotionFormCreatedSuccess')
    );

    yield put(promotionActions.setLoadingSend(false));
  } catch (error) {
    yield put(promotionActions.setLoadingSend(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* deleteItem({ id }: any) {
  try {
    const { isConfirmed } = yield call(
      notification.confirm,
      formatMessage('commonLabelExclude'),
      formatMessage('commonLabelExcludeItem')
    );
    if (isConfirmed) {
      yield put(promotionActions.setLoadingSend(true));
      yield call(promotionService.deletePromotion, id);
      yield put(promotionActions.setLoadingSend(false));

      yield put(promotionActions.getAllRequest(PROMOTION_LIST.REGISTERED));
      yield call(notification.success, formatMessage('promotionFormExcludeSuccess'));
    }
  } catch (error) {
    yield put(promotionActions.setLoadingSend(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* editItem({ id }: any) {
  yield call(history.push, `/engine/promotion/edit/${id}`);
}

export function* editImportedItem({ id }: any) {
  yield call(history.push, `/engine/promotion/edit/import/${id}`);
}

export default all([
  takeLatest(GET_ALL_REQUEST, getAll),
  takeLatest(GET_BY_ID_REQUEST, getById),
  takeLatest(GET_BY_ID_REQUEST_IMPORT, getByIdImportPromotion),
  takeLatest(SAVE_REQUEST, save),
  takeLatest(SAVE_IMPORT_REQUEST, saveImported),
  takeLatest(DELETE_ITEM, deleteItem),
  takeLatest(EDIT_ITEM, editItem),
  takeLatest(EDIT_IMPORTED_ITEM, editImportedItem),
  takeLatest(GET_FILTER_PRODUCT_REQUEST, queryProducts),
  takeLatest(GET_FILTER_CLIENT_REQUEST, queryClients),
  takeLatest(GET_FILTER_REWARDS_REQUEST, getRewards)
]);
